import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import './SearchableSelect.css';

const norm = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/**
 * Select recherchable avec groupes d'options.
 * groups : [{ label, items: [{ value, label }] }]
 * otherOption : { value, label } optionnel, affiché en fin de liste.
 */
export default function SearchableSelect({
  value, onChange, groups, placeholder, otherOption, ariaLabel, compact,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      searchRef.current?.focus();
    }
  }, [open]);

  const allItems = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const current = allItems.find((i) => i.value === value);
  const currentLabel = current ? current.label : (otherOption && value === otherOption.value ? otherOption.label : '');

  const q = norm(query);
  const visible = q
    ? groups
        .map((g) => ({
          ...g,
          items: g.items.filter((i) => norm(i.label).includes(q) || norm(g.label).includes(q)),
        }))
        .filter((g) => g.items.length)
    : groups;
  const showOther = otherOption && (!q || norm(otherOption.label).includes(q));

  const pick = (v) => {
    onChange(v);
    setOpen(false);
  };

  return (
    <div className={`ss-select${open ? ' is-open' : ''}${compact ? ' is-compact' : ''}`} ref={ref}>
      <button
        type="button"
        className="ss-btn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className={currentLabel ? 'ss-name' : 'ss-placeholder'}>{currentLabel || placeholder}</span>
        <ChevronDown size={14} className="ss-caret" aria-hidden="true" />
      </button>
      {open && (
        <div className="ss-panel">
          <div className="ss-search">
            <Search size={13} aria-hidden="true" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              placeholder="Rechercher…"
              aria-label="Rechercher"
            />
          </div>
          <ul className="ss-list" role="listbox" aria-label={ariaLabel}>
            {visible.map((g) => (
              <li key={g.label || 'group'}>
                {g.label && <p className="ss-group">{g.label}</p>}
                {g.items.map((i) => (
                  <button
                    key={i.value}
                    type="button"
                    role="option"
                    aria-selected={i.value === value}
                    className={`ss-option${i.value === value ? ' is-selected' : ''}`}
                    onClick={() => pick(i.value)}
                  >
                    {i.label}
                  </button>
                ))}
              </li>
            ))}
            {showOther && (
              <li>
                <button
                  type="button"
                  role="option"
                  aria-selected={value === otherOption.value}
                  className={`ss-option ss-option-other${value === otherOption.value ? ' is-selected' : ''}`}
                  onClick={() => pick(otherOption.value)}
                >
                  {otherOption.label}
                </button>
              </li>
            )}
            {!visible.length && !showOther && <li className="ss-empty">Aucun résultat</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
